/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import React, {
	forwardRef,
	lazy,
	type ReactNode,
	startTransition,
	Suspense,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { useIntl } from 'react-intl';
import Loadable from 'react-loadable';
import { di } from 'react-magnetic-di';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import type { Placement } from '@atlaskit/dropdown-menu/types';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import BookOpenIcon from '@atlaskit/icon-lab/core/book-open';
import FolderAddIcon from '@atlaskit/icon-lab/core/folder-add';
import SkillIcon from '@atlaskit/icon-lab/core/skill';
import AddIcon from '@atlaskit/icon/core/add';
import CrossIcon from '@atlaskit/icon/core/cross';
import LinkIcon from '@atlaskit/icon/core/link';
import MentionIcon from '@atlaskit/icon/core/mention';
import PageIcon from '@atlaskit/icon/core/page';
import Lozenge from '@atlaskit/lozenge';
import { fg } from '@atlaskit/platform-feature-flags';
import { functionWithCondition } from '@atlaskit/platform-feature-flags-react';
import Popup, { type PopupComponentProps } from '@atlaskit/popup';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import {
	type ExternalContextBrowserProps,
	type PromptInputContext,
	type PromptInputContextMenuItem,
} from '@atlassian/conversation-assistant-external-context/types';
import { useExternalContextBrowser } from '@atlassian/conversation-assistant-external-context/utils';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { GettingStartedTourSpotlight } from '@atlassian/conversation-assistant-onboarding/ui/GettingStartedTour';
import { type Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { useChatContextBrowserContext } from '@atlassian/conversation-assistant-store/controllers/chat-context/store';

import type { BrowserContextSanitizer } from '../../../types';
import { useConversationExternalAttachments } from '../../controllers/conversation-external-attachments';
import { useConversationFiles } from '../../controllers/conversation-files';
import { useFileUploadTrigger } from '../../controllers/file-upload-trigger/useFileUploadTrigger';
import { type FileUploadAssistanceOverrides } from '../../types/FileUploadAssistanceOverrides';
import { AGENTS_FETCH_LIMIT } from '../../utils/agent-mention-config';
import mergeRefs from '../../utils/merge-refs';

import { messages as agentMessages } from './agent-section/messages';
import { useCurrentPageAsContext } from './current-page-as-context';
import { FileUploadDropdownItem } from './file-upload-dropdown-item';
import { MenuItem } from './menu-item';
import { messages } from './messages';

const LoadableAgentSection = Loadable({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "@atlassian/conversation-assistant-chat-prompt-input/agent-section" */ './agent-section'
			)
		).AgentSection,
	loading: () => null,
});

const LoadableRecommendedAgentsPreloader = Loadable({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "@atlassian/conversation-assistant-chat-prompt-input/recommended-agents-preloader" */ './agent-section/recommended-agents-preloader'
			)
		).RecommendedAgentsPreloader,
	loading: () => null,
});

const LazyAgentSection = lazy(() =>
	import(
		/* webpackChunkName: "@atlassian/conversation-assistant-chat-prompt-input/agent-section" */ './agent-section'
	).then((module) => {
		return {
			default: module.AgentSection,
		};
	}),
);

const LazyRecommendedAgentsPreloader = lazy(() =>
	import(
		/* webpackChunkName: "@atlassian/conversation-assistant-chat-prompt-input/recommended-agents-preloader" */ './agent-section/recommended-agents-preloader'
	).then((module) => {
		return {
			default: module.RecommendedAgentsPreloader,
		};
	}),
);

const styles = cssMap({
	menuContainer: {
		display: 'flex',
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		flexDirection: 'column',
		alignItems: 'stretch',
		gap: token('space.050'),
		backgroundColor: token('elevation.surface'),
		minWidth: '240px',
		maxWidth: '300px',
	},
	menuSection: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.025'),
	},
	sectionHeader: {
		font: token('font.heading.xxsmall'),
		color: token('color.text.subtlest'),
		paddingInline: token('space.100'),
		paddingBlock: token('space.075'),
	},
	divider: {
		height: '1px',
		backgroundColor: token('color.background.neutral.subtle'),
		marginBlock: token('space.075'),
	},
	menuPopupContainer: {
		borderRadius: token('radius.large'),
		border: `${token('border.width')} solid ${token('color.border')}`,
		boxShadow: token('elevation.shadow.overlay'),
		overflow: 'hidden',
		zIndex: 600,
	},
	newLozengeBadge: {
		display: 'flex',
		paddingInline: token('space.050'),
		alignItems: 'flex-start',
	},
});

const PopupComponent = forwardRef<HTMLDivElement, PopupComponentProps>((props, ref) => (
	<Box
		{...props}
		ref={ref}
		xcss={styles.menuPopupContainer}
		// PopupComponent's data-testid is of type string, while Box only accepts undefined
		data-testid={undefined}
	/>
));

export type MenuItemsVisibility = {
	fileUpload?: boolean;
	mention?: boolean;
	format?: boolean;
	/**
	 * Controls visibility of the "Add link" menu item.
	 * Defaults to true. If all items are false, the trigger button is hidden entirely.
	 */
	link?: boolean;
};

type ChatInputActionButtonsProps = {
	entryPoint?: string;
	siteId: string;
	useCurrentPageContext: boolean;
	onLinkTypeAheadClick: () => void;
	onMentionTypeAheadClick: () => void;
	onAddTypeAheadClick: () => void;
	onDiscoverSkillsClick?: () => void;
	onBrowsePromptLibraryClick?: () => void;
	onCreateSpaceClick?: () => void;
	hasSkillInPrompt?: boolean;
	/**
	 * When true, an agent is @-mentioned in the input. Items that conflict
	 * with the mentioned agent (currently `View all skills`) are rendered
	 * disabled. Other menu items stay interactive. Defaults to false.
	 */
	isAgentMentionActive?: boolean;
	assistanceServiceParams: AssistanceServiceInputParam['assistanceServiceParams'];
	fileUploadAssistanceOverrides?: FileUploadAssistanceOverrides;
	isDisabled: boolean;
	placement?: Placement;
	attachmentsAndCurrentPageAsContextStoreKey: string;
	onMenuOpen?: () => void;
	onAgentSelect?: (agent: Agent) => void;
	isBrowseAgentsModalOpen?: boolean;
	setIsBrowseAgentsModalOpen?: (isBrowseAgentsModalOpen: boolean) => void;
	shouldRenderInPortal?: boolean;
	enableBrowserContextBanner?: boolean;
	browserContextSanitizer?: BrowserContextSanitizer;
	menuItemsVisibility?: MenuItemsVisibility;
} & ExternalContextBrowserProps;

const NOOP_FILE_UPLOAD_TRIGGER_RESULT: ReturnType<typeof useFileUploadTrigger> = [
	{ handlers: [] },
	{ register: () => {}, unregister: () => {}, openFileDialog: () => {} },
];
const noopFileUploadTrigger = (): ReturnType<typeof useFileUploadTrigger> =>
	NOOP_FILE_UPLOAD_TRIGGER_RESULT;

const useFileUploadTriggerWithCondition = functionWithCondition(
	() =>
		FeatureGates.getExperimentValue('jfind-rovo-for-jira-onboarding', 'isEnabled', false, {
			fireExperimentExposure: false, // exposure fired in configuration loader on Jira
		}),
	useFileUploadTrigger,
	noopFileUploadTrigger,
);

export const ChatInputActionButtons = ({
	entryPoint,
	siteId,
	useCurrentPageContext,
	onLinkTypeAheadClick,
	onMentionTypeAheadClick,
	onAddTypeAheadClick,
	onDiscoverSkillsClick,
	onBrowsePromptLibraryClick,
	onCreateSpaceClick,
	hasSkillInPrompt = false,
	isAgentMentionActive = false,
	assistanceServiceParams,
	fileUploadAssistanceOverrides,
	contextProviders,
	onContextBrowserShow,
	onContextAvailable,
	isDisabled,
	placement = 'top-start',
	attachmentsAndCurrentPageAsContextStoreKey: attachmentsStoreAndCurrentPageContextKey,
	onMenuOpen,
	onAgentSelect,
	isBrowseAgentsModalOpen = false,
	setIsBrowseAgentsModalOpen = () => {},
	shouldRenderInPortal = false,
	enableBrowserContextBanner = false,
	browserContextSanitizer,
	menuItemsVisibility: externalMenuItemsVisibility = {},
}: ChatInputActionButtonsProps): JSX.Element | null => {
	const menuItemsVisibility = {
		fileUpload: externalMenuItemsVisibility?.fileUpload ?? true,
		mention: externalMenuItemsVisibility?.mention ?? true,
		format: externalMenuItemsVisibility?.format ?? true,
		link: externalMenuItemsVisibility?.link ?? true,
	};

	// If every controllable menu item is explicitly hidden, don't render the trigger button at all.
	const hasAnyVisibleItem = Object.values(menuItemsVisibility).some(Boolean);

	di(useCurrentPageAsContext, useConversationFiles, useChatContextBrowserContext);
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const { setUseCurrentPageContextUserPreference, currentContext } = useCurrentPageAsContext();
	const [browserContext] = useChatContextBrowserContext();
	const showAddContextButton = useCurrentPageContext;

	// Only consider browser context available if enableBrowserContextBanner config is enabled (otherwise behave as non-extension)
	const hasBrowserContext = !!browserContext?.context?.browserUrl && enableBrowserContextBanner;

	// Check if browser context URL is blocklisted (only when enableBrowserContextBanner is true and sanitizer is provided)
	const isBrowserContextBlocklisted =
		enableBrowserContextBanner &&
		browserContextSanitizer &&
		hasBrowserContext &&
		browserContext?.context?.browserUrl
			? browserContextSanitizer.isBlocklisted(browserContext.context.browserUrl)
			: false;

	// Button should be disabled only if:
	// 1. Current context type is 'none' (page not supported)
	// 2. AND no browser context is available (can't use external site context)
	// If browser context is available, enable the button even if currentContext.type === 'none' (unless blocklisted)
	const isContextDisabled =
		isBrowserContextBlocklisted || (currentContext.type === 'none' && !hasBrowserContext);

	let discoverSkillsButtonTooltipContent: string | undefined;
	if (hasSkillInPrompt) {
		discoverSkillsButtonTooltipContent = formatMessage(
			messages.discoverSkillsButtonDisabledTooltip,
		);
	} else if (isAgentMentionActive) {
		discoverSkillsButtonTooltipContent = formatMessage(
			messages.discoverSkillsButtonAgentMentionDisabledTooltip,
		);
	}

	const [menuState, setMenuState] = useState<'main' | 'external-context'>('main');
	const [currentContextBrowser, setCurrentContextBrowser] = useState<ReactNode | null>(null);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const { addExternalContext } = useConversationExternalAttachments(
		attachmentsStoreAndCurrentPageContextKey,
	);
	const returnFocusRef = useRef<HTMLElement>(null);

	const assistanceServiceParamsForFileUpload = useMemo(
		() => ({
			...assistanceServiceParams,
			...fileUploadAssistanceOverrides,
		}),
		[assistanceServiceParams, fileUploadAssistanceOverrides],
	);

	const inputRef = useRef<HTMLInputElement>(null);
	const { getFileInput } = useConversationFiles({
		assistanceServiceParams: {
			...assistanceServiceParamsForFileUpload,
			touchpointSource: 'chatInputActionButtons',
		},
		key: attachmentsStoreAndCurrentPageContextKey,
	});

	/**
	 * SPICE-1518 - Rovo for Jira Onboarding
	 */
	const isRovoForJiraOnboardingExperiment = FeatureGates.getExperimentValue(
		'jfind-rovo-for-jira-onboarding',
		'isEnabled',
		false,
		{
			fireExperimentExposure: false, // exposure fired in configuration loader on Jira
		},
	);
	const [, { register: registerFileOpenHandler, unregister: unregisterFileOpenHandler }] =
		useFileUploadTriggerWithCondition();

	useEffect(() => {
		if (!isRovoForJiraOnboardingExperiment) return;

		const handler = () => inputRef.current?.click();
		registerFileOpenHandler(handler);
		return () => {
			unregisterFileOpenHandler(handler);
		};
	}, [isRovoForJiraOnboardingExperiment, registerFileOpenHandler, unregisterFileOpenHandler]);

	const { getMenuItems } = useExternalContextBrowser({
		contextProviders,
		onContextBrowserShow: (browser: ReactNode) => {
			if (browser) {
				setCurrentContextBrowser(browser);
				setMenuState('external-context');
			} else {
				setCurrentContextBrowser(null);
				setMenuState('main');
			}
			onContextBrowserShow?.(browser);
		},
		onContextAvailable: (contexts: PromptInputContext[]) => {
			setMenuState('main');
			setCurrentContextBrowser(null);
			setIsDropdownOpen(false);
			// Add external contexts to unified store
			addExternalContext(attachmentsStoreAndCurrentPageContextKey, contexts);
			onContextAvailable?.(contexts);
		},
	});

	const focusOnClose = (
		event?: Event | React.MouseEvent | React.KeyboardEvent | KeyboardEvent | null | undefined,
	) => {
		// Refocus on the trigger when closing the popup except for native mouse events
		if (!event || (event as MouseEvent).detail === 0) {
			requestAnimationFrame(() => {
				returnFocusRef?.current?.focus();
			});
		}
	};

	const externalMenuItems = getMenuItems();

	// Hide the entire trigger button when every controllable menu item is turned off.
	// All hooks are called above unconditionally (rules of hooks). The early return is
	// safe here because it comes after all hook calls.
	// Note: externalMenuItems (3rd-party context providers) are not counted here because
	// they are dynamic; the consumer controls visibility via menuItemsVisibility only.
	if (!hasAnyVisibleItem) {
		return null;
	}

	return (
		<>
			<Popup
				isOpen={isDropdownOpen}
				onClose={() => {
					setIsDropdownOpen(false);
					setMenuState('main');
					setCurrentContextBrowser(null);
					focusOnClose();
				}}
				placement={placement}
				shouldRenderToParent={!shouldRenderInPortal}
				shouldFlip={!shouldRenderInPortal}
				popupComponent={PopupComponent}
				zIndex={600}
				trigger={(triggerProps) => (
					<GettingStartedTourSpotlight step={1} entryPoint={entryPoint}>
						<IconButton
							{...triggerProps}
							onClick={(e, analyticsEvent) => {
								sendAnalyticsEvent(
									analyticsEvent.update({
										actionSubjectId: 'rovoChatAddMenuTrigger',
									}),
								);
								setIsDropdownOpen(!isDropdownOpen);
								if (!isDropdownOpen) {
									onMenuOpen?.();
								}
							}}
							isTooltipDisabled={false}
							isSelected={isDropdownOpen}
							appearance="subtle"
							icon={isDropdownOpen ? CrossIcon : AddIcon}
							label={formatMessage(messages.chatInputActionsButtonsTooltip)}
							ref={mergeRefs([triggerProps.ref, returnFocusRef])}
							isDisabled={isDisabled}
						/>
					</GettingStartedTourSpotlight>
				)}
				content={() => (
					<Box role="menu" xcss={styles.menuContainer}>
						{menuState === 'main' && (
							<>
								<Box xcss={styles.menuSection} role="group">
									{onCreateSpaceClick && fg('rovo_chat_spaces_fe') && (
										<MenuItem
											icon={<FolderAddIcon label="" />}
											label={formatMessage(messages.createSpaceButtonLabel)}
											onClick={() => {
												onCreateSpaceClick();
												sendAnalyticsEvent({
													action: 'clicked',
													actionSubject: 'button',
													actionSubjectId: 'rovoChatCreateSpaceButton',
												});
												setIsDropdownOpen(false);
											}}
										/>
									)}
									{onBrowsePromptLibraryClick &&
										FeatureGates.getExperimentValue(
											'rovo_chat_prompt_library_v2_exp',
											'isEnabled',
											false,
										) && (
											<MenuItem
												icon={<BookOpenIcon label="" />}
												label={formatMessage(messages.browsePromptLibraryButtonLabel)}
												onClick={() => {
													startTransition(() => {
														onBrowsePromptLibraryClick();
													});
													sendAnalyticsEvent({
														action: 'clicked',
														actionSubject: 'button',
														actionSubjectId: 'libraryOpenedFromSidebar',
														attributes: {
															source: 'browseInputMenuButton',
														},
													});
													setIsDropdownOpen(false);
												}}
												badge={
													fg('rovo_chat_prompt_library_new_badge') ? (
														<Box xcss={styles.newLozengeBadge}>
															<Lozenge appearance="discovery">
																{formatMessage(messages.browsePromptLibraryNewBadge)}
															</Lozenge>
														</Box>
													) : undefined
												}
											/>
										)}
									{onDiscoverSkillsClick && fg('rovo_skills_directory_ui') && (
										<Tooltip
											position="top"
											content={discoverSkillsButtonTooltipContent}
											canAppear={() => hasSkillInPrompt || isAgentMentionActive}
										>
											<MenuItem
												icon={<SkillIcon label="" />}
												label={formatMessage(messages.discoverSkillsButtonLabel)}
												isDisabled={hasSkillInPrompt || isAgentMentionActive}
												onClick={() => {
													startTransition(() => {
														onDiscoverSkillsClick();
													});
													sendAnalyticsEvent({
														action: 'clicked',
														actionSubject: 'button',
														actionSubjectId: 'rovoChatDiscoverSkillsButton',
													});
													setIsDropdownOpen(false);
												}}
											/>
										</Tooltip>
									)}
									{menuItemsVisibility.fileUpload && (
										<FileUploadDropdownItem
											attachmentsStoreKey={attachmentsStoreAndCurrentPageContextKey}
											onClick={() => {
												inputRef.current?.click();
												setIsDropdownOpen(false);
											}}
											assistanceServiceParams={assistanceServiceParamsForFileUpload}
										/>
									)}
									{menuItemsVisibility.link && (
										<MenuItem
											icon={<LinkIcon label="" />}
											label={formatMessage(messages.linkTypeAheadButtonLabel)}
											onClick={() => {
												onLinkTypeAheadClick();
												sendAnalyticsEvent({
													action: 'clicked',
													actionSubject: 'button',
													actionSubjectId: 'rovoChatAddLinkButton',
												});
												setIsDropdownOpen(false);
											}}
										/>
									)}
									{externalMenuItems.map((item: PromptInputContextMenuItem) => (
										<MenuItem
											key={item.key}
											icon={item.icon}
											label={item.label}
											onClick={() => {
												item.action();
												sendAnalyticsEvent({
													action: 'clicked',
													actionSubject: 'button',
													actionSubjectId: `rovoChatExternalContext_${item.key}`,
												});
											}}
										/>
									))}
									{menuItemsVisibility.mention && (
										<MenuItem
											icon={<MentionIcon label="" />}
											label={formatMessage(messages.mentionTypeAheadButtonLabel)}
											onClick={() => {
												onMentionTypeAheadClick();
												sendAnalyticsEvent({
													action: 'clicked',
													actionSubject: 'button',
													actionSubjectId: 'rovoChatMentionButton',
												});
												setIsDropdownOpen(false);
											}}
										/>
									)}
									{menuItemsVisibility.format && (
										<MenuItem
											icon={<AddIcon label="" />}
											label={formatMessage(messages.addMoreFormattingButtonLabel)}
											onClick={() => {
												onAddTypeAheadClick();
												sendAnalyticsEvent({
													action: 'clicked',
													actionSubject: 'button',
													actionSubjectId: 'rovoChatAddFormattingButton',
												});
												setIsDropdownOpen(false);
											}}
										/>
									)}
									{showAddContextButton && (
										<Tooltip
											position="top"
											content={formatMessage(messages.addCurrentPageAsContextDisabledTooltip)}
											canAppear={() => isContextDisabled}
										>
											<MenuItem
												icon={<PageIcon label="" />}
												label={formatMessage(messages.addContentButtonLabel)}
												isDisabled={isContextDisabled}
												onClick={() => {
													setUseCurrentPageContextUserPreference(
														attachmentsStoreAndCurrentPageContextKey,
														true,
													);
													sendAnalyticsEvent({
														action: 'clicked',
														actionSubject: 'button',
														actionSubjectId: 'rovoChatAddContextButton',
													});
													setIsDropdownOpen(false);
												}}
											/>
										</Tooltip>
									)}
								</Box>
								{onAgentSelect && (
									<>
										<Box xcss={styles.divider} />
										<Box xcss={styles.sectionHeader}>
											{formatMessage(agentMessages.agentsHeader)}
										</Box>
										<Box xcss={styles.menuSection} role="group">
											{fg('aimate_conversation_assistant_async_loading') ? (
												<Suspense fallback={null}>
													<LazyAgentSection
														assistanceServiceParams={assistanceServiceParams}
														onAgentSelect={onAgentSelect}
														siteId={siteId}
														isBrowseAgentsModalOpen={isBrowseAgentsModalOpen}
														setIsBrowseAgentsModalOpen={setIsBrowseAgentsModalOpen}
														setIsDropdownOpen={setIsDropdownOpen}
													/>
												</Suspense>
											) : (
												<LoadableAgentSection
													assistanceServiceParams={assistanceServiceParams}
													onAgentSelect={onAgentSelect}
													siteId={siteId}
													isBrowseAgentsModalOpen={isBrowseAgentsModalOpen}
													setIsBrowseAgentsModalOpen={setIsBrowseAgentsModalOpen}
													setIsDropdownOpen={setIsDropdownOpen}
												/>
											)}
										</Box>
									</>
								)}
							</>
						)}
						{menuState === 'external-context' && currentContextBrowser && (
							<Box xcss={styles.menuSection} role="group">
								{currentContextBrowser}
							</Box>
						)}
					</Box>
				)}
				shouldUseCaptureOnOutsideClick={true}
			/>
			{menuItemsVisibility.fileUpload && getFileInput(inputRef)}
			{onAgentSelect && <AgentSectionPreloader assistanceServiceParams={assistanceServiceParams} />}
		</>
	);
};

const AgentSectionPreloader = ({
	assistanceServiceParams,
}: {
	assistanceServiceParams: AssistanceServiceInputParam['assistanceServiceParams'];
}) => {
	// Preload the bundle
	useEffect(() => {
		if (!fg('aimate_conversation_assistant_async_loading')) {
			LoadableAgentSection.preload();
		}
	}, []);

	// Align preloader's cache key with AgentSection's when the @<agent> picker is on.
	const limit = fg('rovo_chat_agent_selection') ? AGENTS_FETCH_LIMIT : undefined;

	// Preload the data
	if (fg('aimate_conversation_assistant_async_loading')) {
		return (
			<Suspense fallback={null}>
				<LazyRecommendedAgentsPreloader
					assistanceServiceParams={assistanceServiceParams}
					limit={limit}
				/>
			</Suspense>
		);
	}
	return (
		<LoadableRecommendedAgentsPreloader
			assistanceServiceParams={assistanceServiceParams}
			limit={limit}
		/>
	);
};
